import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Stack,
  Chip,
  Divider,
  Avatar,
  IconButton,
  Alert,
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { personsAPI } from '../../services/api';

export interface DirectorItem {
  id: string;
  name: string;
  relationId?: string;
  roleTitle?: string;
  isNominee?: boolean;
  profilePhotoUrl?: string;
}

interface CompanyDirectorsSectionProps {
  directors: DirectorItem[];
  onRemoveDirector: (relationId: string, name: string) => void;
}

const CompanyDirectorsSection: React.FC<CompanyDirectorsSectionProps> = ({
  directors,
  onRemoveDirector,
}) => (
  <Card sx={{ borderRadius: 2 }}>
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WorkIcon color="primary" />
        Directors
        <Chip label={directors.length} size="small" color="primary" />
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {directors.length === 0 ? (
        <Alert severity="info">No directors found.</Alert>
      ) : (
        <List disablePadding>
          {directors.map(({ id: personId, name, relationId, roleTitle, isNominee, profilePhotoUrl }, idx) => {
            const initials =
              name
                ?.split(' ')
                .map((n: string) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase() || '?';
            return (
              <React.Fragment key={personId}>
                {idx > 0 && <Divider />}
                <Box display="flex" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <ListItemButton
                    component={RouterLink}
                    to={`/persons/${encodeURIComponent(personId)}`}
                    sx={{ flex: 1, px: 0, minWidth: 0, alignItems: 'flex-start', py: { xs: 1.25, sm: 1 } }}
                  >
                    <Avatar
                      src={profilePhotoUrl ? personsAPI.getProfilePhotoUrl(personId) : undefined}
                      sx={{
                        width: 36,
                        height: 36,
                        mr: 2,
                        bgcolor: 'primary.main',
                        fontSize: '0.875rem',
                      }}
                    >
                      {initials}
                    </Avatar>
                    <ListItemText
                      disableTypography
                      sx={{ minWidth: 0, mr: 1 }}
                      primary={
                        <Typography variant="body2" fontWeight="medium" sx={{ overflowWrap: 'anywhere' }}>
                          {name}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                          <Chip
                            label={roleTitle || 'Director'}
                            size="small"
                            variant="outlined"
                          />
                          {isNominee && (
                            <Chip
                              label="Nominee"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      }
                    />
                  </ListItemButton>
                  {relationId && (
                    <IconButton
                      aria-label="Remove director"
                      size="small"
                      color="error"
                      sx={{ minWidth: 40, minHeight: 40, mt: { xs: 0.75, sm: 0 } }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemoveDirector(relationId, name ?? '');
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </React.Fragment>
            );
          })}
        </List>
      )}
    </CardContent>
  </Card>
);

export default CompanyDirectorsSection;
